import { useCallback, useEffect, useRef } from 'react';

export function useDebounce<T>(
  callback: (params?: unknown) => Promise<T> | T,
  delay: number,
): (params?: unknown) => Promise<T | undefined> {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedCallback = useCallback(
    async (params?: unknown) => {
      let callbackResult: T | undefined;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(async () => {
        try {
          callbackResult = await callbackRef.current(params);
        } catch {
          callbackResult = undefined;
        }
      }, delay);

      return callbackResult;
    },
    [delay],
  );

  return debouncedCallback;
}
